import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, Calendar, Phone, MapPin, Instagram } from "lucide-react";
import { CALENDLY_URL, INTEREST_OPTIONS } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  organisation: "",
  role: "",
  email: "",
  interest: "Schools",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't send right now. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="contact-section"
      id="contact"
      className="bg-[#F8F9FA] border-t-2 border-[#1A1A1A] relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left */}
          <div className="lg:col-span-5">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#FF6600]">
              Contact
            </p>
            <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
              Ready to build{" "}
              <span className="bg-[#FF6600] text-white px-2 inline-block">
                what comes next?
              </span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#4A4A4A] font-medium leading-relaxed max-w-md">
              Whether you are a school, library, council or education
              organisation, we’d love to explore how TinkerShift can
              support your next stage.
            </p>

            <div className="mt-8 space-y-3 max-w-md">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-calendly-card"
                className="flex items-start gap-4 bg-white border-2 border-[#1A1A1A] rounded-2xl p-4 nb-shadow-sm nb-press"
              >
                <span className="h-11 w-11 rounded-xl bg-[#0055FF] text-white border-2 border-[#1A1A1A] flex items-center justify-center">
                  <Calendar size={20} strokeWidth={2.5} />
                </span>
                <span>
                  <span className="block font-bold text-base">
                    Book a Discovery Call
                  </span>
                  <span className="block text-sm text-[#4A4A4A]">
                    30 min · with Julija · Free
                  </span>
                </span>
              </a>
              <a
                href="mailto:hello@tinkertank.rocks"
                data-testid="contact-email-card"
                className="flex items-start gap-4 bg-white border-2 border-[#1A1A1A] rounded-2xl p-4 nb-shadow-sm nb-press"
              >
                <span className="h-11 w-11 rounded-xl bg-[#FF007F] text-white border-2 border-[#1A1A1A] flex items-center justify-center">
                  <Mail size={20} strokeWidth={2.5} />
                </span>
                <span>
                  <span className="block font-bold text-base">
                    hello@tinkertank.rocks
                  </span>
                  <span className="block text-sm text-[#4A4A4A]">
                    For partnerships and press
                  </span>
                </span>
              </a>
              <a
                href="tel:+61455400261"
                data-testid="contact-phone-card"
                className="flex items-start gap-4 bg-white border-2 border-[#1A1A1A] rounded-2xl p-4 nb-shadow-sm nb-press"
              >
                <span className="h-11 w-11 rounded-xl bg-[#00C2B8] text-[#1A1A1A] border-2 border-[#1A1A1A] flex items-center justify-center">
                  <Phone size={20} strokeWidth={2.5} />
                </span>
                <span>
                  <span className="block font-bold text-base">
                    0455 400 261
                  </span>
                  <span className="block text-sm text-[#4A4A4A]">
                    Call or text, mon–fri
                  </span>
                </span>
              </a>
              <div
                data-testid="contact-studio-card"
                className="flex items-start gap-4 bg-white border-2 border-[#1A1A1A] rounded-2xl p-4 nb-shadow-sm"
              >
                <span className="h-11 w-11 rounded-xl bg-[#FFD700] text-[#1A1A1A] border-2 border-[#1A1A1A] flex items-center justify-center">
                  <MapPin size={20} strokeWidth={2.5} />
                </span>
                <span>
                  <span className="block font-bold text-base">
                    50 Yeo Street, Neutral Bay NSW
                  </span>
                  <span className="block text-sm text-[#4A4A4A]">
                    The TinkerTank studio
                  </span>
                </span>
              </div>
              <a
                href="https://instagram.com/_tinkertank_"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-card"
                className="flex items-start gap-4 bg-white border-2 border-[#1A1A1A] rounded-2xl p-4 nb-shadow-sm nb-press"
              >
                <span className="h-11 w-11 rounded-xl bg-[#FF6600] text-white border-2 border-[#1A1A1A] flex items-center justify-center">
                  <Instagram size={20} strokeWidth={2.5} />
                </span>
                <span>
                  <span className="block font-bold text-base">
                    @_tinkertank_
                  </span>
                  <span className="block text-sm text-[#4A4A4A]">
                    Follow the maker journey
                  </span>
                </span>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border-2 border-[#1A1A1A] rounded-3xl p-6 md:p-8 nb-shadow-lg"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name" required>
                  <input
                    required
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-name-input"
                    className="form-input"
                    placeholder="Your full name"
                  />
                </Field>
                <Field label="Organisation" required>
                  <input
                    required
                    value={form.organisation}
                    onChange={update("organisation")}
                    data-testid="contact-organisation-input"
                    className="form-input"
                    placeholder="School / Library / Council"
                  />
                </Field>
                <Field label="Role" required>
                  <input
                    required
                    value={form.role}
                    onChange={update("role")}
                    data-testid="contact-role-input"
                    className="form-input"
                    placeholder="Principal, Librarian, Director..."
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-email-input"
                    className="form-input"
                    placeholder="you@org.edu"
                  />
                </Field>
                <Field label="I'm interested in" className="md:col-span-2">
                  <div className="flex flex-wrap gap-2">
                    {INTEREST_OPTIONS.map((opt) => (
                      <button
                        type="button"
                        key={opt}
                        onClick={() => setForm({ ...form, interest: opt })}
                        data-testid={`contact-interest-${opt.toLowerCase()}`}
                        className={`px-4 py-2 rounded-full border-2 text-sm font-bold uppercase tracking-wide transition-all ${
                          form.interest === opt
                            ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                            : "bg-white text-[#1A1A1A] border-[#1A1A1A] nb-shadow-sm nb-press"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Message" required className="md:col-span-2">
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-message-input"
                    className="form-input resize-y"
                    placeholder="Tell us about your school, library or program. What are you hoping to shift?"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="mt-7 inline-flex items-center gap-2 bg-[#0055FF] text-white border-2 border-[#1A1A1A] rounded-full px-7 py-4 font-bold uppercase tracking-wide nb-shadow nb-press disabled:opacity-70"
              >
                {submitting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <ArrowUpRight size={18} strokeWidth={2.5} />
                )}
                {submitting ? "Sending..." : "Send & Book a Call"}
              </button>
              <p className="mt-3 text-xs text-[#4A4A4A]">
                By submitting, you’ll also receive a direct booking link to
                schedule your discovery call.
              </p>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: #fff;
          border: 2px solid #1A1A1A;
          border-radius: 14px;
          padding: 12px 14px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          color: #1A1A1A;
          outline: none;
          transition: box-shadow 150ms ease, transform 150ms ease;
        }
        .form-input::placeholder { color: #999; font-weight: 500; }
        .form-input:focus {
          box-shadow: 4px 4px 0 0 #1A1A1A;
          transform: translate(-1px, -1px);
        }
      `}</style>
    </section>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[11px] uppercase tracking-widest font-bold text-[#1A1A1A]">
        {label}
        {required && <span className="text-[#FF007F]"> *</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
